import { Client, Provider, cacheExchange, fetchExchange } from 'urql';
import { authExchange } from '@urql/exchange-auth';
import { FC, ReactNode, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getJWTTokens, setJWTokens } from '@shared/libs/local-storage';
import { useAuth } from '@features/auth/hooks/useAuth';
import { isTokenExpired } from '@shared/libs/jwt';
import { refreshAccessToken } from '@features/auth/api/refresh';
import { getGraphqlEndpoint, isPublicGraphqlOperation } from '@shared/api/api-url';

type TUrQlProvider = { children?: ReactNode };

const PRIVATE_GRAPHQL_ENDPOINT = getGraphqlEndpoint(false);
const PUBLIC_GRAPHQL_ENDPOINT = getGraphqlEndpoint(true);

const AUTH_ERROR_MESSAGES = new Set(['session not found', 'invalid token']);

const didAuthError = (error: { graphQLErrors: Array<{ message: string }> }) =>
  error.graphQLErrors.some((graphQLError) => AUTH_ERROR_MESSAGES.has(graphQLError.message));

const routingFetch: typeof fetch = async (_input, init) => {
  if (init?.body && typeof init.body === 'string') {
    try {
      const { operationName } = JSON.parse(init.body) as { operationName?: string };

      if (isPublicGraphqlOperation(operationName)) {
        return fetch(PUBLIC_GRAPHQL_ENDPOINT, init);
      }
    } catch {
      console.error('Ошибка роутинга api');
    }
  }

  return fetch(PRIVATE_GRAPHQL_ENDPOINT, init);
};

export const UrQlProvider: FC<TUrQlProvider> = ({ children }) => {
  const { logout, login } = useAuth();

  const client = useMemo(() => {
    return new Client({
      url: PRIVATE_GRAPHQL_ENDPOINT,
      fetch: routingFetch,
      exchanges: [
        cacheExchange,
        authExchange(async (utils) => ({
          // Проврка, истёк access-токен или нет
          willAuthError() {
            const { accessToken } = getJWTTokens();
            return !!accessToken && isTokenExpired(accessToken);
          },

          // Если тоекна нет или он просрочен, то не добавляем Authorization
          addAuthToOperation(operation) {
            const { accessToken } = getJWTTokens();
            if (!accessToken || isTokenExpired(accessToken)) {
              return operation;
            }
            return utils.appendHeaders(operation, {
              Authorization: `Bearer ${accessToken}`,
            });
          },
          didAuthError,

          // Если токен истёк, обонвляется
          async refreshAuth() {
            const { refreshToken, email } = getJWTTokens();

            if (!refreshToken || isTokenExpired(refreshToken)) {
              logout();
              return;
            }

            try {
              const newTokens = await refreshAccessToken(refreshToken);
              const { group_name } = jwtDecode<{ group_name: string }>(newTokens.access_token);
              setJWTokens(email ?? '', newTokens.access_token, newTokens.refresh_token, group_name);
              login(email ?? '', newTokens.access_token, newTokens.refresh_token, group_name);
            } catch {
              logout();
            }
          },
        })),

        fetchExchange,
      ],
    });
  }, [logout, login]);

  return <Provider value={client}>{children}</Provider>;
};
